import React from "react";

function flattenChildText(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flattenChildText).join("");
  if (React.isValidElement(node)) return flattenChildText(node.props.children);
  return "";
}

function isOptionElement(child) {
  if (!React.isValidElement(child)) return false;
  const type = child.type;
  if (type === "option") return true;
  if (typeof type === "string" && type.toLowerCase() === "option") return true;
  return false;
}

function isFragmentElement(child) {
  if (!React.isValidElement(child)) return false;
  return child.type === React.Fragment;
}

/** Walk Fragment/array children and collect native <option> elements. */
function collectOptionElements(children, out = []) {
  React.Children.forEach(children, (child) => {
    if (child == null || child === false) return;

    if (Array.isArray(child)) {
      child.forEach((item) => collectOptionElements(item, out));
      return;
    }

    if (!React.isValidElement(child)) return;

    if (isOptionElement(child)) {
      const value = child.props.value ?? "";
      // Placeholder <option value=""> — handled by react-select placeholder + clear button
      if (String(value) === "") return;

      const label = flattenChildText(child.props.children).trim() || String(value);
      const title = child.props.title ? String(child.props.title) : "";
      out.push({
        value: String(value),
        label,
        searchText: [label, title, String(value)].filter(Boolean).join(" "),
      });
      return;
    }

    if (isFragmentElement(child)) {
      collectOptionElements(child.props.children, out);
    }
  });
  return out;
}

/** Convert <option> children (including inside Fragments) to react-select options. */
export function childrenToSelectOptions(children) {
  return collectOptionElements(children, []);
}

export function normalizeSelectOptions(options) {
  if (!Array.isArray(options)) return [];
  return options.map((opt) => {
    const value = String(opt?.value ?? "");
    const label = String(opt?.label ?? value);
    return {
      value,
      label,
      searchText: opt?.searchText || [label, value].filter(Boolean).join(" "),
    };
  });
}

export function resolveSelectOptions({ options, children }) {
  if (Array.isArray(options) && options.length > 0) {
    return normalizeSelectOptions(options);
  }
  return childrenToSelectOptions(children);
}

export function filterSelectOption(option, inputValue) {
  const input = (inputValue || "").trim().toLowerCase();
  if (!input) return true;
  const data = option.data || option;
  const haystack = String(
    data.searchText || data.label || option.label || ""
  ).toLowerCase();
  return haystack.includes(input);
}

export function findSelectedOption(options, value) {
  const strValue = value === undefined || value === null ? "" : String(value);
  if (strValue === "") return null;
  return options.find((o) => o.value === strValue) || null;
}
